import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, X, Camera, AlertCircle, Loader2, Search, Keyboard } from 'lucide-react';
import { api, ScanResult } from '../lib/api';
import jsQR from 'jsqr';

interface QrScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScanResult?: (result: ScanResult) => void;
  title?: string;
  hint?: string;
  navigateOnScan?: boolean;
}

export default function QrScanner({ isOpen, onClose, onScanResult, title, hint, navigateOnScan = true }: QrScannerProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'camera' | 'manual'>('camera');
  const [manualCode, setManualCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanningRef = useRef(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    scanningRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setManualCode('');
    setError(null);
    onClose();
  }, [stopCamera, onClose]);

  const searchCode = useCallback(async (code: string) => {
    if (!code.trim() || isSearching) return;
    
    setIsSearching(true);
    setError(null);
    
    try {
      const result = await api.scanQrCode(code.trim().toUpperCase());
      
      if (onScanResult) {
        // External callback mode
        onScanResult(result);
        if (!navigateOnScan) {
          // Don't close automatically in callback mode unless navigating
          setIsSearching(false);
          return;
        }
      }
      
      if (result.found && navigateOnScan) {
        handleClose();
        if (result.type === 'asset') {
          navigate(`/assets/${result.id}`);
        } else if (result.type === 'location') {
          navigate(`/locations/${result.id}`);
        }
      } else if (!result.found) {
        setError(`Codice "${code}" non trovato`);
      }
    } catch (err) {
      setError('Errore durante la ricerca del codice');
    } finally {
      setIsSearching(false);
    }
  }, [isSearching, handleClose, navigate, onScanResult, navigateOnScan]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        scanningRef.current = true;
        
        // Wait for video to be ready and start playing
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play();
          }
        };
        
        // Start scanning loop with jsQR
        const scan = () => {
          if (!scanningRef.current || !videoRef.current || !canvasRef.current) return;
          
          const video = videoRef.current;
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          
          if (ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Use jsQR to detect QR code
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'dontInvert'
            });
            
            if (code && code.data) {
              // QR code detected! Stop scanning and search
              scanningRef.current = false;
              searchCode(code.data);
              return;
            }
          }
          
          // Continue scanning at ~15fps for performance
          if (scanningRef.current) {
            setTimeout(() => requestAnimationFrame(scan), 66);
          }
        };
        
        // Start scanning when video is ready
        scan();
      }
    } catch (err) {
      console.error('Camera error:', err);
      setHasCamera(false);
      setMode('manual');
    }
  }, [searchCode]);

  useEffect(() => {
    if (isOpen && mode === 'camera' && hasCamera) {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [isOpen, mode, hasCamera, startCamera, stopCamera]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    searchCode(manualCode);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-slideInUp">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
              <QrCode className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">{title || 'Scansiona QR'}</h2>
              <p className="text-xs text-gray-500">{hint || 'Cerca asset o ubicazione'}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setMode('camera')}
            disabled={!hasCamera}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              mode === 'camera'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            } ${!hasCamera ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Camera className="w-4 h-4" />
            Fotocamera
          </button>
          <button
            onClick={() => { stopCamera(); setMode('manual'); }}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              mode === 'manual'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Keyboard className="w-4 h-4" />
            Manuale
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {mode === 'camera' && hasCamera ? (
            <div className="space-y-4">
              <div className="relative aspect-square bg-gray-900 rounded-xl overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-primary-500 rounded-lg relative">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary-500 rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary-500 rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary-500 rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary-500 rounded-br-lg" />
                    <div className="absolute inset-0 bg-gradient-to-b from-primary-500/20 via-transparent to-primary-500/20 animate-pulse" />
                  </div>
                </div>
              </div>
              
              <p className="text-center text-sm text-gray-500">
                Inquadra un codice QR oppure{' '}
                <button
                  onClick={() => { stopCamera(); setMode('manual'); }}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  inseriscilo manualmente
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div className="form-group">
                <label className="form-label">Codice QR</label>
                <div className="relative">
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                    placeholder="Es. UNIT0001 o ELET0003"
                    className="input pr-10 font-mono uppercase"
                    autoFocus
                  />
                  <QrCode className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Inserisci il codice che trovi sull'etichetta QR
                </p>
              </div>

              <button
                type="submit"
                disabled={!manualCode.trim() || isSearching}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Ricerca in corso...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Cerca
                  </>
                )}
              </button>
            </form>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700 animate-shake">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer help */}
        <div className="px-4 pb-4">
          <div className="p-3 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-600">
              <strong>Formati supportati:</strong>
            </p>
            <ul className="text-xs text-gray-500 mt-1 space-y-0.5">
              <li>• Ubicazioni: UNIT0001, AREA0002, WARE0001...</li>
              <li>• Asset: ELET0001, MOBI0002, ATTR0003...</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Floating button component to trigger the scanner
export function QrScannerButton() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-full shadow-lg shadow-primary-500/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        aria-label="Scansiona QR Code"
      >
        <QrCode className="w-5 h-5" />
      </button>
      <QrScanner isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
